/**
 * ZenMarkup Privacy Analytics — List module.
 *
 * Paginated list builder for table widgets (5 items per page).
 */

import { formatNumber } from './charts.js';

/** Maximum rows per page in list widgets. */
const PAGE_SIZE = 5;

/**
 * Render rows for the current page into a list wrapper.
 *
 * @param {HTMLElement}   wrap        The .zmpa-list container.
 * @param {Array}         allRows     Full dataset.
 * @param {number}        page        Current page (0-based).
 * @param {Function|null} onRowClick  Optional callback(name) when a row is clicked.
 */
function renderPage(wrap, allRows, page, onRowClick) {
	// Lock height before removing rows to prevent layout jump.
	if (wrap.offsetHeight > 0) {
		wrap.style.minHeight = wrap.offsetHeight + 'px';
	}

	const old = wrap.querySelectorAll('.zmpa-list-row, .zmpa-pagination');
	old.forEach(function (el) { el.remove(); });

	const start = page * PAGE_SIZE;
	const pageRows = allRows.slice(start, start + PAGE_SIZE);

	pageRows.forEach(function (row) {
		const item = document.createElement('div');
		item.className = 'zmpa-list-row';

		const name = document.createElement('span');
		name.className = 'zmpa-list-name';
		name.textContent = row.name;
		name.title = row.name;

		if (row.hint) {
			const hint = document.createElement('span');
			hint.className = 'zmpa-list-hint';
			hint.textContent = ' (' + row.hint + ')';
			name.appendChild(hint);
		}

		const count = document.createElement('span');
		count.className = 'zmpa-list-count';
		count.textContent = typeof row.count === 'string' ? row.count : formatNumber(row.count);

		item.appendChild(name);
		item.appendChild(count);

		if (onRowClick) {
			item.className += ' zmpa-clickable-row';
			item.addEventListener('click', function () {
				onRowClick(row.name);
			});
		}

		wrap.appendChild(item);
	});

	const totalPages = Math.ceil(allRows.length / PAGE_SIZE);
	if (totalPages > 1) {
		wrap.appendChild(buildPagination(wrap, allRows, page, totalPages, onRowClick));
	}
}

/**
 * Build pagination controls (prev / page info / next).
 *
 * @param {HTMLElement}   wrap        The .zmpa-list container.
 * @param {Array}         allRows     Full dataset.
 * @param {number}        page        Current page (0-based).
 * @param {number}        totalPages  Total number of pages.
 * @param {Function|null} onRowClick  Optional callback for row clicks.
 * @return {HTMLElement}
 */
function buildPagination(wrap, allRows, page, totalPages, onRowClick) {
	const nav = document.createElement('div');
	nav.className = 'zmpa-pagination';

	const content = document.querySelector('[data-el="content"]');
	const iconLeft  = content ? content.getAttribute('data-icon-chevron-left')  : '';
	const iconRight = content ? content.getAttribute('data-icon-chevron-right') : '';

	const prev = document.createElement('button');
	prev.className = 'zmpa-pagination-btn';
	prev.innerHTML = iconLeft + ' Prev';
	prev.disabled = page === 0;
	prev.addEventListener('click', function () {
		renderPage(wrap, allRows, page - 1, onRowClick);
	});

	const info = document.createElement('span');
	info.className = 'zmpa-pagination-info';
	info.textContent = (page + 1) + ' / ' + totalPages;

	const next = document.createElement('button');
	next.className = 'zmpa-pagination-btn';
	next.innerHTML = 'Next ' + iconRight;
	next.disabled = page >= totalPages - 1;
	next.addEventListener('click', function () {
		renderPage(wrap, allRows, page + 1, onRowClick);
	});

	nav.appendChild(prev);
	nav.appendChild(info);
	nav.appendChild(next);

	return nav;
}

/**
 * Build a paginated list from rows of data.
 *
 * @param {string}        colLabel    Header for the name column.
 * @param {string}        colCount    Header for the count column.
 * @param {Array}         rows        Array of { name, count } objects.
 * @param {Function|null} onRowClick  Optional callback(name) when a row is clicked.
 * @return {HTMLElement}
 */
export function build(colLabel, colCount, rows, onRowClick) {
	const wrap = document.createElement('div');
	wrap.className = 'zmpa-list';

	const header = document.createElement('div');
	header.className = 'zmpa-list-header';
	header.innerHTML = '<span>' + colLabel + '</span><span>' + colCount + '</span>';
	wrap.appendChild(header);

	renderPage(wrap, rows, 0, onRowClick || null);

	return wrap;
}
